import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { WorkoutContext } from "../contexts/WorkoutContext";
import { useProgramme } from "../hooks/useProgramme";
import { releaseStuckPointerEvents } from "../utils/pointerEventsGuard";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { LibraryBig, Plus } from "lucide-react";
import ProgrammeDesignForm from "../components/programme/ProgrammeDesignForm";
import ProgrammeHeader from "../components/programme/ProgrammeHeader";
import ProgrammeTimeline from "../components/programme/ProgrammeTimeline";
import ProgrammeLibrarySheet from "../components/programme/ProgrammeLibrarySheet";
import ProgrammeImportView from "../components/programme/ProgrammeImportView";
import ResumeProgrammeSheet from "../components/programme/ResumeProgrammeSheet";
import InstantiateSessionDialog from "../components/programme/InstantiateSessionDialog";
import EditProgrammeSessionDialog from "../components/programme/EditProgrammeSessionDialog";
import WriteSessionSheet from "../components/programme/WriteSessionSheet";
import ProgrammeNextCard from "../components/programme/ProgrammeNextCard";
import ProgrammeCheckInCard from "../components/programme/ProgrammeCheckInCard";
import {
  weekSuggestion,
  lightWeekOutcome,
  movedDeloads,
  currentDeloads,
} from "../utils/programmeWeeks";
import ProgrammeSessionSheet from "../components/programme/ProgrammeSessionSheet";
import {
  ChangeTodaySheet,
  LinkDoneSheet,
  MoveSessionSheet,
  WeekPlanSheet,
} from "../components/programme/WeekPool";
import { slotName, slotWeekday, weekdayOf } from "../utils/programmeSchedule";
import { requestStartToday } from "../utils/setupFlow";
import FirstVisitTip from "../components/help/FirstVisitTip";
import { nextPendingProgrammeSession } from "../utils/programmeContext";
import { coerceLegacyNodeTypes } from "../utils/planNormaliser";
import { useToast } from "../contexts/ToastContext";
import { setProgrammeEquipment } from "../services/api";
import { requestUpdate } from "../serviceWorkerRegistration";
import { useAuth } from "../contexts/AuthContext";
import useProgrammeMates, { matesBuilds } from "../hooks/useProgrammeMates";
import { ProgrammeMatesContext } from "../contexts/ProgrammeMatesContext";
import TogetherSheet from "../components/programme/TogetherSheet";
import { adaptPlanToKit, equipmentList } from "../utils/adaptPlan";

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ProgrammeView = () => {
  const {
    loadSessionIntoCoach,
    setActiveProgrammeSession,
    userProfile,
    setView,
    loadPresetIntoCoach,
  } = useContext(WorkoutContext);
  const { showToast } = useToast();

  // #92: an app build from before the planner moved can't save a plan. The
  // server says so with APP_UPDATE_REQUIRED; take the newest build.
  const handlePlanSaveError = useCallback(
    (err) => {
      if (err?.code !== "APP_UPDATE_REQUIRED") return false;
      showToast({
        title: "Khronos needs to update",
        description: "Your plan wasn't saved. Update, then try again.",
        duration: 0,
        action: { label: "Update", onClick: () => requestUpdate() },
      });
      return true;
    },
    [showToast],
  );

  const {
    programme,
    programmes,
    loading,
    hasActiveProgramme,
    currentWeek,
    stats,
    createProgramme,
    assignSession,
    clearSession,
    reorderWeek,
    placeSession,
    skipSession,
    linkSession,
    resetWeek,
    setDeloads,
    saveCheckin,
    undoCheckin,
    repeatLastWeek,
    undoRepeatLastWeek,
    switchProgramme,
    editProgramme,
    removeProgramme,
    refreshProgramme,
  } = useProgramme();

  const [editTarget, setEditTarget] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  // #125: the paused programme being resumed; its sheet picks the start day.
  const [resumeTarget, setResumeTarget] = useState(null);
  // null | "create" | <programme object> (edit)
  const [formMode, setFormMode] = useState(null);
  // The session sheet (any slot, any state), and the builder that fills a
  // slot: { session, mode: "generate" | "load", auto, start }.
  const [sheetTarget, setSheetTarget] = useState(null);
  const [builder, setBuilder] = useState(null);
  // #173: the slot whose session you're writing yourself.
  const [writeTarget, setWriteTarget] = useState(null);
  // Training together (#184).
  const { user } = useAuth();
  const { group, reload: reloadMates } = useProgrammeMates(programme?.id);
  const matesValue = useMemo(() => (group ? { group, me: user?.id } : null), [group, user?.id]);
  const [togetherOpen, setTogetherOpen] = useState(false);
  const [adapting, setAdapting] = useState(false);

  // A mate's session, as it is: it stays theirs in the "Built by" line.
  const takeMateBuild = useCallback(
    async (session, build) => {
      setSheetTarget(null);
      try {
        await assignSession(session.id, {
          label: build.label || build.plan_data?.label || session.scheduled_label,
          plan_data: build.plan_data,
          built_by: build.user_id,
        });
        showToast({ title: `Using ${build.name.split(" ")[0]}'s session` });
        refreshProgramme();
        return true;
      } catch (err) {
        showToast({ title: "Couldn't use it", description: err.message, variant: "destructive" });
        return false;
      }
    },
    [assignSession, refreshProgramme, showToast],
  );

  // Adapt for me: swap what your kit can't do, and make the copy yours.
  const adaptForMe = useCallback(
    async (session, plan) => {
      setAdapting(true);
      try {
        const { plan: adapted, changes } = await adaptPlanToKit(plan, equipmentList(userProfile));
        await assignSession(session.id, {
          label: adapted.label || session.label || session.scheduled_label,
          plan_data: adapted,
          adapted: true,
        });
        setSheetTarget(null);
        showToast({
          title: "Adapted for you",
          description: changes.length
            ? `${changes.length} swap${changes.length === 1 ? "" : "s"} for your kit.`
            : "It already suits your kit, so it's unchanged. It's yours now.",
        });
        refreshProgramme();
        reloadMates();
      } catch (err) {
        showToast({ title: "Couldn't adapt it", description: err.message, variant: "destructive" });
      } finally {
        setAdapting(false);
      }
    },
    [assignSession, refreshProgramme, reloadMates, showToast, userProfile],
  );

  const nextSlot = useMemo(
    () => (programme ? nextPendingProgrammeSession(programme) : null),
    [programme],
  );

  // The week pool (#196): Change on the Next card, Plan this week, and
  // moving one session from its sheet.
  const [changeOpen, setChangeOpen] = useState(false);
  const [planWeek, setPlanWeek] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const failed = useCallback(
    (err) => showToast({ title: "Couldn't change the week", description: err?.message, variant: "destructive" }),
    [showToast],
  );
  const handlePlace = useCallback(
    async (session, weekday) => {
      try {
        await placeSession(session.id, weekday);
        showToast({
          title:
            weekday == null
              ? `${slotName(session)} is in this week's pool`
              : `${slotName(session)} moved to ${DAY_SHORT[weekday]}`,
        });
      } catch (err) {
        failed(err);
      }
    },
    [placeSession, showToast, failed],
  );
  const handleSkip = useCallback(
    async (session) => {
      try {
        await skipSession(session.id);
        showToast({ title: `${slotName(session)} skipped this week` });
      } catch (err) {
        failed(err);
      }
    },
    [skipSession, showToast, failed],
  );
  const handleLink = useCallback(
    async (slot, done) => {
      try {
        await linkSession(slot.id, done.id);
        showToast({ title: `Counted as ${slotName(slot)}` });
      } catch (err) {
        failed(err);
      }
    },
    [linkSession, showToast, failed],
  );
  // Do another of this week's sessions today: it goes on today, and what was
  // planned goes into the pool, with a one-tap way to its old day.
  const handlePickToday = useCallback(
    async (picked) => {
      const planned = nextSlot;
      setChangeOpen(false);
      if (!planned) return;
      const today = weekdayOf(new Date());
      const oldDay = slotWeekday(programme, { ...picked, week_number: planned.week_number });
      try {
        await placeSession(picked.id, today);
        await placeSession(planned.id, null);
        showToast({
          title: `${slotName(picked)} is on today`,
          description: `${slotName(planned)} is in this week's pool.`,
          ...(oldDay != null && oldDay > today
            ? {
                action: {
                  label: `Put ${slotName(planned)} on ${DAY_SHORT[oldDay]}`,
                  onClick: () => placeSession(planned.id, oldDay).catch(failed),
                },
                duration: 8000,
              }
            : {}),
        });
      } catch (err) {
        failed(err);
      }
    },
    [nextSlot, programme, placeSession, showToast, failed],
  );
  // Something else today: what was planned goes into the pool first.
  const somethingElse = useCallback(
    async (go) => {
      setChangeOpen(false);
      if (nextSlot) {
        try {
          await placeSession(nextSlot.id, null);
        } catch (err) {
          failed(err);
          return;
        }
      }
      requestAnimationFrame(go);
    },
    [nextSlot, placeSession, failed],
  );

  // Refresh programme data when this view becomes active
  useEffect(() => {
    if (hasActiveProgramme) {
      refreshProgramme();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Run a slot's plan in the Coach, tagged to the slot so completing it
  // completes the slot.
  const startSlot = useCallback(
    (session, plan = session?.plan_data) => {
      if (!programme || !session || !plan) return;
      setActiveProgrammeSession({
        programmeId: programme.id,
        programmeSessionId: session.id,
        label: plan.label || session.label,
        scheduledLabel: session.scheduled_label,
      });
      loadSessionIntoCoach(
        coerceLegacyNodeTypes(JSON.parse(JSON.stringify(plan))),
      );
    },
    [programme, setActiveProgrammeSession, loadSessionIntoCoach],
  );

  // Close the sheet, then open the next layer on the following frame so Radix
  // finishes tearing the sheet down first (#52 pattern).
  const fromSheet = useCallback((fn) => {
    setSheetTarget(null);
    requestAnimationFrame(() => {
      fn();
      releaseStuckPointerEvents(document);
    });
  }, []);

  const handleStartNext = useCallback(
    (slot) => {
      // First to build shares it (#184): a mate's build runs as it is.
      const offer = group ? matesBuilds(group, slot)[0] : null;
      if (slot.plan_data) startSlot(slot);
      else if (offer) takeMateBuild(slot, offer).then((ok) => ok && startSlot(slot, offer.plan_data));
      else setBuilder({ session: slot, mode: "generate", auto: true, start: true });
    },
    [startSlot, group, takeMateBuild],
  );

  const handleSessionClick = useCallback((session) => {
    setSheetTarget(session);
  }, []);

  const handleEditSession = useCallback((session) => {
    setEditTarget(session);
  }, []);

  const handleSaveEditedSession = useCallback(
    async ({ label, plan_data }) => {
      if (!editTarget) return;
      await assignSession(editTarget.id, { label, plan_data });
    },
    [editTarget, assignSession],
  );

  // #173: a session you wrote yourself, saved to its slot (then run).
  const handleSaveWritten = useCallback(
    async (plan) => {
      if (!writeTarget) return;
      await assignSession(writeTarget.id, { label: plan.label, plan_data: plan });
      setWriteTarget(null);
    },
    [writeTarget, assignSession],
  );
  const handleStartWritten = useCallback(
    async (plan) => {
      if (!writeTarget) return;
      const slot = writeTarget;
      await assignSession(slot.id, { label: plan.label, plan_data: plan });
      setWriteTarget(null);
      startSlot(slot, plan);
    },
    [writeTarget, assignSession, startSlot],
  );

  // Save the (possibly edited) plan to the slot, then load it straight into the
  // coach to run — so editing and starting are one flow, not two routes.
  const handleStartSession = useCallback(
    async ({ label, plan_data }) => {
      if (!editTarget || !programme) return;
      try {
        await assignSession(editTarget.id, { label, plan_data });
      } catch (err) {
        console.error("[Programme] Failed to save before starting:", err);
      }
      setActiveProgrammeSession({
        programmeId: programme.id,
        programmeSessionId: editTarget.id,
        label,
        scheduledLabel: editTarget.scheduled_label,
      });
      setEditTarget(null);
      loadSessionIntoCoach(plan_data);
    },
    [
      editTarget,
      programme,
      assignSession,
      setActiveProgrammeSession,
      loadSessionIntoCoach,
    ],
  );

  const handleClearSession = useCallback(
    (session) => clearSession(session.id).catch(() => {}),
    [clearSession],
  );

  const handleReorder = useCallback(
    (weekNumber, orderedIds) => reorderWeek(weekNumber, orderedIds).catch(() => {}),
    [reorderWeek],
  );

  // #52 guard: deleting/resetting/opening a form can swap this view (unmounting
  // Radix layers mid-close); hard-restore the body pointer-events lock if Radix
  // left it stuck. `useBodyPointerEventsGuard` in App.js catches whatever slips
  // past this (#72) — this stays as the immediate, same-gesture restore.
  const restoreBodyPointer = () =>
    requestAnimationFrame(() => {
      releaseStuckPointerEvents(document);
    });

  // #94: the weekly check-in. Khronos spots a light week (or two hard ones)
  // and asks; nothing changes without a tap, and a moved deload has Undo.
  const suggestion = useMemo(
    // An imported programme runs as written, so it has no check-ins to act on.
    () => (programme && programme.source !== "imported" ? weekSuggestion(programme, currentWeek) : null),
    [programme, currentWeek],
  );
  const [checkInBusy, setCheckInBusy] = useState(false);

  const runCheckIn = useCallback(
    async ({ week, answer, action, deloads, message, repeatWeek = false }) => {
      setCheckInBusy(true);
      const before = deloads ? currentDeloads(programme) : null;
      let repeated = null;
      try {
        if (deloads) await setDeloads(deloads, userProfile);
        if (repeatWeek) repeated = await repeatLastWeek();
        await saveCheckin(week, { answer, action });
        if (message) {
          showToast({
            title: message,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  if (before) await setDeloads(before, userProfile);
                  if (repeated) await undoRepeatLastWeek(repeated);
                  await undoCheckin(week);
                } catch (err) {
                  console.error("[Programme] Undo failed:", err);
                }
              },
            },
          });
        }
      } catch (err) {
        if (!handlePlanSaveError(err)) console.error("[Programme] Check-in failed:", err);
      } finally {
        setCheckInBusy(false);
      }
    },
    [programme, setDeloads, saveCheckin, undoCheckin, repeatLastWeek, undoRepeatLastWeek, userProfile, showToast, handlePlanSaveError],
  );

  const handleLightAnswer = useCallback(
    (answer, { countAsDeload = false, repeatWeek = false } = {}) => {
      const outcome = lightWeekOutcome(suggestion, answer, { countAsDeload, repeatWeek });
      return runCheckIn({
        week: suggestion.week,
        answer,
        action: outcome.action,
        deloads: outcome.move ? movedDeloads(programme, outcome.move) : null,
        message: outcome.message,
        repeatWeek: Boolean(outcome.repeatWeek),
      });
    },
    [suggestion, programme, runCheckIn],
  );

  const handleHardAnswer = useCallback(
    (accept) => {
      if (!accept) {
        return runCheckIn({ week: suggestion.week, answer: "hard", action: "none" });
      }
      const deloads = suggestion.nextDeload
        ? movedDeloads(programme, { from: suggestion.nextDeload, to: currentWeek })
        : [...currentDeloads(programme), currentWeek];
      return runCheckIn({
        week: suggestion.week,
        answer: "hard",
        action: "deload_forward",
        deloads,
        message: suggestion.nextDeload
          ? `Week ${currentWeek} is now your deload, instead of week ${suggestion.nextDeload}.`
          : `Week ${currentWeek} is now a deload week.`,
      });
    },
    [suggestion, programme, currentWeek, runCheckIn],
  );

  // #92: "Repeat last week" from the programme menu, with Undo.
  const handleRepeatLastWeek = useCallback(async () => {
    try {
      const repeated = await repeatLastWeek();
      if (!repeated) return;
      showToast({
        title: `Week ${repeated.week} repeats this week, and the rest of the programme moves a week later.`,
        action: {
          label: "Undo",
          onClick: () => undoRepeatLastWeek(repeated).catch((err) => console.error("[Programme] Undo failed:", err)),
        },
      });
    } catch (err) {
      console.error("[Programme] Repeat week failed:", err);
    } finally {
      restoreBodyPointer();
    }
  }, [repeatLastWeek, undoRepeatLastWeek, showToast]);

  // #72: resetting a week replaces the entire programme object, unmounting the
  // header's dropdown → alert-dialog stack and every timeline row's dropdown in
  // one commit — the same teardown that hung the UI on delete (#52). Defer the
  // restore the same way, and swallow the failure so a rejected reset can't
  // escape as an unhandled rejection.
  const handleResetWeek = useCallback(
    async (weekNumber) => {
      try {
        await resetWeek(weekNumber, userProfile);
      } catch (err) {
        if (!handlePlanSaveError(err)) {
          console.error("[Programme] Failed to reset week:", err);
        }
      } finally {
        restoreBodyPointer();
      }
    },
    [resetWeek, userProfile, handlePlanSaveError],
  );

  // Open the create/edit form from the library. Close the library dialog FIRST,
  // then swap to the full-page form on the next frame so Radix finishes its
  // close teardown before this view unmounts the dialog (#52 pattern).
  const openForm = useCallback((mode) => {
    setLibraryOpen(false);
    requestAnimationFrame(() => {
      setFormMode(mode);
      restoreBodyPointer();
    });
  }, []);

  const handleDeleteProgramme = useCallback(
    async (target) => {
      try {
        await removeProgramme(target?.id);
      } catch (err) {
        console.error("[Programme] Failed to delete programme:", err);
      } finally {
        restoreBodyPointer();
      }
    },
    [removeProgramme],
  );

  // #58: programme library actions
  const handleSwitchProgramme = useCallback((p) => setResumeTarget(p), []);
  const handleResume = useCallback(
    (firstDay) => {
      const target = resumeTarget;
      setResumeTarget(null);
      if (target) switchProgramme(target.id, firstDay).catch(() => {});
    },
    [resumeTarget, switchProgramme],
  );
  const resumeSheet = (
    <ResumeProgrammeSheet
      programme={resumeTarget}
      onOpenChange={(open) => !open && setResumeTarget(null)}
      onResume={handleResume}
    />
  );

  const handleFormSubmit = useCallback(
    async (config) => {
      try {
        if (formMode && typeof formMode === "object") {
          await editProgramme(formMode.id, config);
        } else {
          await createProgramme(config);
        }
        setFormMode(null);
      } catch (err) {
        // Keep the form open on an update prompt so nothing is lost before
        // the user chooses to update.
        if (handlePlanSaveError(err)) return;
        console.error("[Programme] Save failed:", err);
        setFormMode(null);
      }
    },
    [formMode, editProgramme, createProgramme, handlePlanSaveError],
  );

  // Loading state
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto pt-8 space-y-4">
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  // Create / edit form (library "New" / "Edit", or first-run create)
  if (formMode === "import") {
    return (
      <ProgrammeImportView
        onCancel={() => setFormMode(null)}
        onSave={async (payload) => {
          await createProgramme(payload);
          setFormMode(null);
        }}
      />
    );
  }

  if (formMode) {
    const editing = typeof formMode === "object" ? formMode : null;
    return (
      <ProgrammeDesignForm
        programme={editing || undefined}
        onSubmit={handleFormSubmit}
        onCancel={() => setFormMode(null)}
      />
    );
  }

  // First run — no programmes at all: straight to the create form.
  if (!hasActiveProgramme && programmes.length === 0) {
    return <ProgrammeDesignForm onSubmit={handleFormSubmit} onImport={() => openForm("import")} />;
  }

  // Have a library but nothing active (e.g. the active one was deleted).
  if (!hasActiveProgramme) {
    return (
      <>
        <div className="max-w-2xl mx-auto pt-16 text-center space-y-4">
          <p className="text-muted-foreground">
            No active programme. Pick one from your library or start a new one.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => setLibraryOpen(true)}>
              <LibraryBig className="mr-2 h-4 w-4" />
              My Programmes ({programmes.length})
            </Button>
            <Button onClick={() => openForm("create")}>
              <Plus className="mr-2 h-4 w-4" />
              New programme
            </Button>
          </div>
        </div>
        <ProgrammeLibrarySheet
          open={libraryOpen}
          onOpenChange={setLibraryOpen}
          programmes={programmes}
          activeId={programme?.id}
          onSwitch={handleSwitchProgramme}
          onEdit={(p) => openForm(p)}
          onDelete={handleDeleteProgramme}
          onNew={() => openForm("create")}
          onImport={() => openForm("import")}
        />
        {resumeSheet}
      </>
    );
  }

  // An imported programme runs as written: nothing re-plans its weeks.
  const fixed = programme?.source === "imported";

  // Active programme: header and next session, then the weeks. Two columns
  // on a wide screen so the next session stays in view beside the weeks.
  return (
    <div className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)] gap-5 pb-8 pt-4 md:pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-start lg:gap-x-8">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-5 lg:sticky lg:top-20">
        <ProgrammeHeader
          programme={programme}
          currentWeek={currentWeek}
          stats={stats}
          onDeleteProgramme={handleDeleteProgramme}
          onResetWeek={fixed ? undefined : handleResetWeek}
          onRepeatLastWeek={currentWeek > 1 && !fixed ? handleRepeatLastWeek : undefined}
          onOpenLibrary={() => setLibraryOpen(true)}
          onEditProgramme={fixed ? undefined : () => openForm(programme)}
          onNewProgramme={() => openForm("create")}
          onTrainTogether={() => setTogetherOpen(true)}
          onSetEquipment={(presetId) =>
            setProgrammeEquipment(programme.id, presetId)
              .then(() => refreshProgramme())
              .catch(() => showToast({ title: "Couldn't change the equipment" }))
          }
          group={group}
        />
        <FirstVisitTip
          id="programme"
          title="How your programme works"
          topic="programmes#building-and-starting-sessions"
        >
          Your next session is at the top: Start runs it, or builds it first.
          Tap any session in the weeks to build, start or edit it.
        </FirstVisitTip>
        <ProgrammeCheckInCard
          key={suggestion ? `${suggestion.type}-${suggestion.week}` : "none"}
          suggestion={suggestion}
          onLightAnswer={handleLightAnswer}
          onHardAnswer={handleHardAnswer}
          busy={checkInBusy}
        />
        {nextSlot ? (
          <ProgrammeNextCard
            programme={programme}
            slot={nextSlot}
            onStart={handleStartNext}
            onView={handleSessionClick}
            onChange={nextSlot?.week_number === currentWeek ? () => setChangeOpen(true) : undefined}
            mateBuilder={
              nextSlot && !nextSlot.plan_data && group
                ? matesBuilds(group, nextSlot)[0]?.name.split(" ")[0] || null
                : null
            }
          />
        ) : (
          <div className="rounded-2xl border border-secondary/40 bg-secondary/10 p-4">
            <p className="font-semibold text-white">Programme complete</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Every session is done or past. Start a new programme from the ⋯
              menu when you're ready.
            </p>
          </div>
        )}
      </div>

      <div className="min-w-0">
        <ProgrammeMatesContext.Provider value={matesValue}>
          <ProgrammeTimeline
            programme={programme}
            currentWeek={currentWeek}
            nextSessionId={nextSlot?.id}
            onSessionClick={handleSessionClick}
            onEditSession={handleEditSession}
            onClearSession={handleClearSession}
            onReorder={handleReorder}
            onPlanWeek={setPlanWeek}
          />
        </ProgrammeMatesContext.Provider>
      </div>

      <ProgrammeLibrarySheet
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        programmes={programmes}
        activeId={programme?.id}
        onSwitch={handleSwitchProgramme}
        onEdit={(p) => openForm(p)}
        onDelete={handleDeleteProgramme}
        onNew={() => openForm("create")}
        onImport={() => openForm("import")}
      />
      {resumeSheet}

      <ProgrammeSessionSheet
        open={!!sheetTarget}
        onOpenChange={(open) => {
          if (!open) setSheetTarget(null);
        }}
        programme={programme}
        session={sheetTarget}
        onStart={(session) => fromSheet(() => startSlot(session))}
        onEdit={(session) => fromSheet(() => setEditTarget(session))}
        onRebuild={(session) =>
          fromSheet(() => setBuilder({ session, mode: "generate" }))
        }
        onBuild={(session) =>
          fromSheet(() => setBuilder({ session, mode: "generate", auto: true }))
        }
        onCustomise={(session) =>
          fromSheet(() => setBuilder({ session, mode: "generate" }))
        }
        onWrite={(session) => fromSheet(() => setWriteTarget(session))}
        onMove={(session) => fromSheet(() => setMoveTarget(session))}
        onUseSaved={(session) =>
          fromSheet(() => setBuilder({ session, mode: "load" }))
        }
        group={group}
        me={user?.id}
        onUseMateBuild={takeMateBuild}
        onAdapt={adaptForMe}
        adapting={adapting}
      />

      <ChangeTodaySheet
        open={changeOpen}
        onOpenChange={setChangeOpen}
        programme={programme}
        slot={nextSlot}
        onPick={handlePickToday}
        onBuild={() =>
          somethingElse(() => {
            requestStartToday("customise", {});
            setView("session");
          })
        }
        onFree={() => somethingElse(() => loadPresetIntoCoach("free"))}
      />
      <WeekPlanSheet
        open={planWeek != null}
        onOpenChange={(open) => !open && setPlanWeek(null)}
        programme={programme}
        weekNumber={planWeek}
        onPlace={handlePlace}
        onSkip={handleSkip}
        onLink={handleLink}
      />
      <MoveSessionSheet
        open={!!moveTarget && !linkTarget}
        onOpenChange={(open) => !open && setMoveTarget(null)}
        programme={programme}
        session={moveTarget}
        onPlace={handlePlace}
        onSkip={handleSkip}
        onCountDone={(s) => setLinkTarget(s)}
      />
      <LinkDoneSheet
        open={!!linkTarget}
        onOpenChange={(open) => {
          if (!open) {
            setLinkTarget(null);
            setMoveTarget(null);
          }
        }}
        onBack={() => setLinkTarget(null)}
        programme={programme}
        slot={linkTarget}
        onLink={handleLink}
      />

      <TogetherSheet
        open={togetherOpen}
        onOpenChange={(open) => {
          setTogetherOpen(open);
          if (!open) reloadMates();
        }}
        programme={programme}
        group={group}
      />

      <InstantiateSessionDialog
        open={!!builder}
        onOpenChange={(open) => {
          if (!open) setBuilder(null);
        }}
        targetSession={builder?.session}
        programme={programme}
        onAssignSession={assignSession}
        mode={builder?.mode}
        autoGenerate={Boolean(builder?.auto)}
        onAssigned={
          builder?.start ? (plan) => startSlot(builder.session, plan) : undefined
        }
      />

      {writeTarget && (
        <WriteSessionSheet
          programme={programme}
          session={writeTarget}
          onClose={() => setWriteTarget(null)}
          onSave={handleSaveWritten}
          onStart={handleStartWritten}
        />
      )}

      <EditProgrammeSessionDialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        programme={programme}
        session={editTarget}
        onSave={handleSaveEditedSession}
        onStart={handleStartSession}
      />
    </div>
  );
};

export default ProgrammeView;
